import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PerformanceAnalytics = ({ analyticsData }) => {
  const [activeChart, setActiveChart] = useState('monthly');

  const chartTypes = [
    { id: 'monthly', label: 'Monthly Returns', icon: 'Calendar' },
    { id: 'heatmap', label: 'Performance Heatmap', icon: 'Grid3X3' },
    { id: 'drawdown', label: 'Drawdown Analysis', icon: 'TrendingDown' }
  ];

  const monthlyReturns = [
    { month: 'Jan', return: 12.5, trades: 45 },
    { month: 'Feb', return: -3.2, trades: 38 },
    { month: 'Mar', return: 8.7, trades: 52 },
    { month: 'Apr', return: 15.3, trades: 41 },
    { month: 'May', return: -1.8, trades: 33 },
    { month: 'Jun', return: 22.1, trades: 48 },
    { month: 'Jul', return: 7.9, trades: 39 },
    { month: 'Aug', return: -5.4, trades: 29 },
    { month: 'Sep', return: 18.6, trades: 56 },
    { month: 'Oct', return: 11.2, trades: 44 },
    { month: 'Nov', return: 9.8, trades: 37 },
    { month: 'Dec', return: 14.7, trades: 42 }
  ];

  const performanceHeatmap = [
    { day: 'Mon', hour0: 2.1, hour6: 1.8, hour12: -0.5, hour18: 3.2 },
    { day: 'Tue', hour0: 1.5, hour6: 2.3, hour12: 1.7, hour18: -1.1 },
    { day: 'Wed', hour0: 0.8, hour6: 3.1, hour12: 2.4, hour18: 1.9 },
    { day: 'Thu', hour0: 2.7, hour6: 1.2, hour12: -0.8, hour18: 2.6 },
    { day: 'Fri', hour0: 1.9, hour6: 2.8, hour12: 3.5, hour18: 0.7 },
    { day: 'Sat', hour0: 0.3, hour6: 1.1, hour12: 1.8, hour18: 2.2 },
    { day: 'Sun', hour0: 1.4, hour6: 0.9, hour12: 2.1, hour18: 1.6 }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 trading-shadow">
          <p className="text-sm text-muted-foreground mb-1">{label}</p>
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              Return: <span className={data?.return >= 0 ? 'text-success' : 'text-error'}>
                {data?.return >= 0 ? '+' : ''}{data?.return}%
              </span>
            </p>
            <p className="text-sm font-medium text-foreground">
              Trades: <span className="text-primary">{data?.trades}</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderMonthlyChart = () => (
    <div className="w-full h-80" aria-label="Monthly Returns Chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={monthlyReturns} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
          <XAxis 
            dataKey="month" 
            stroke="#B0B0B0"
            fontSize={12}
            tickLine={false}
            axisLine={false}
          />
          <YAxis 
            stroke="#B0B0B0"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => `${value}%`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="return" radius={[4, 4, 0, 0]}>
            {monthlyReturns?.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry?.return >= 0 ? '#00FF88' : '#FF4444'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const renderHeatmap = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-5 gap-2 text-xs text-muted-foreground">
        <div></div>
        <div className="text-center">00:00</div>
        <div className="text-center">06:00</div>
        <div className="text-center">12:00</div>
        <div className="text-center">18:00</div>
      </div>
      
      {performanceHeatmap?.map((row, rowIndex) => (
        <div key={rowIndex} className="grid grid-cols-5 gap-2">
          <div className="text-xs text-muted-foreground py-2">{row?.day}</div>
          {['hour0', 'hour6', 'hour12', 'hour18']?.map((hour, colIndex) => {
            const value = row?.[hour];
            const intensity = Math.abs(value) / 4; // Normalize to 0-1
            const isPositive = value >= 0;
            return (
              <div
                key={colIndex}
                className={`h-8 rounded-md flex items-center justify-center text-xs font-medium cursor-help group relative ${
                  isPositive 
                    ? 'bg-success text-success-foreground' 
                    : 'bg-error text-error-foreground'
                }`}
                style={{ 
                  opacity: 0.3 + (intensity * 0.7)
                }}
              >
                {value > 0 ? '+' : ''}{value}%
                                <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-popover border border-border rounded text-xs opacity-0 group-hover:opacity-100 pointer-events-none trading-transition z-50">
                  {row?.day} {hour?.replace('hour', '')}:00 - {value > 0 ? '+' : ''}{value}%
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );

  const renderDrawdownChart = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="TrendingDown" size={16} className="text-error" />
            <span className="text-sm font-medium text-foreground">Max Drawdown</span>
          </div>
          <div className="text-2xl font-bold text-error">-8.5%</div>
          <div className="text-xs text-muted-foreground">Aug 15, 2024</div>
        </div>
        
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Clock" size={16} className="text-warning" />
            <span className="text-sm font-medium text-foreground">Recovery Time</span>
          </div>
          <div className="text-2xl font-bold text-warning">12 days</div>
          <div className="text-xs text-muted-foreground">Average: 8 days</div>
        </div>
        
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="BarChart3" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Calmar Ratio</span>
          </div>
          <div className="text-2xl font-bold text-primary">2.85</div>
          <div className="text-xs text-muted-foreground">Excellent</div>
        </div>
      </div>

      <div className="bg-muted/50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground mb-3">Drawdown Periods</h4>
        <div className="space-y-2">
          {[
            { period: 'Aug 2024', duration: '12 days', depth: '-8.5%', recovery: 'Sep 2024' },
            { period: 'May 2024', duration: '8 days', depth: '-4.2%', recovery: 'May 2024' },
            { period: 'Feb 2024', duration: '15 days', depth: '-6.1%', recovery: 'Mar 2024' }
          ]?.map((drawdown, index) => (
            <div key={index} className="flex items-center justify-between py-2 border-b border-border-secondary last:border-b-0">
              <div>
                <div className="text-sm font-medium text-foreground">{drawdown?.period}</div>
                <div className="text-xs text-muted-foreground">{drawdown?.duration}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-error">{drawdown?.depth}</div>
                <div className="text-xs text-muted-foreground">Recovered: {drawdown?.recovery}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderChartContent = () => {
    switch (activeChart) {
      case 'monthly': return renderMonthlyChart();
      case 'heatmap': return renderHeatmap();
      case 'drawdown': return renderDrawdownChart();
      default: return renderMonthlyChart();
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-8">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground mb-2">Performance Analytics</h2>
          <p className="text-sm text-muted-foreground">Advanced performance metrics and visualizations</p>
        </div>
        
        <Button variant="ghost" size="sm" iconName="Download">
          Export Analytics
        </Button>
      </div>
      {/* Chart Type Selector */}
      <div className="flex flex-wrap items-center space-x-1 mb-6 bg-muted rounded-lg p-1">
        {chartTypes?.map((chart) => (
          <button
            key={chart?.id}
            onClick={() => setActiveChart(chart?.id)}
            className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-md trading-transition ${
              activeChart === chart?.id
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={chart?.icon} size={16} />
            <span className="hidden sm:inline">{chart?.label}</span>
          </button>
        ))}
      </div>
      {/* Chart Content */}
      <div className="min-h-80">
        {renderChartContent()}
      </div>
    </div>
  );
};

export default PerformanceAnalytics;